// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Images
import heroImage from "@/assets/hero-strategic.png";
import pedSeal from "@/assets/ped.png";
import eedSeal from "@/assets/eed.png";
import ctaImage from "@/assets/cta-atlas.png";

export const Route = createFileRoute("/$lang/strategic-certification")({
  head: () => ({
    meta: [
      { title: i18n.t("strategic-certification:metaTitle", "Strategic Company Certification — BIZU Space") },
      {
        name: "description",
        content: i18n.t("strategic-certification:metaDesc",
          "BIZU Space is officially recognized as a Strategic Company (EED) with Strategic Products (PED) by the Brazilian Ministry of Defense.",
        ),
      },
      {
        property: "og:title",
        content: i18n.t("strategic-certification:metaTitle", "Strategic Company Certification — BIZU Space"),
      },
      {
        property: "og:description",
        content: i18n.t("strategic-certification:metaOgDesc",
          "Officially certified as EED and PED, validating our aerospace technologies for Brazil's strategic autonomy.",
        ),
      },
      { property: "og:url", content: "/strategic-certification" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/strategic-certification" }],
  }),
  component: StrategicCertificationPage,
});

function StrategicCertificationPage() {
  const { t } = useTranslation("strategic-certification");

  const pedPoints = [
    t("ped.p1", "Contribute to strategic autonomy"),
    t("ped.p2", "Strengthen national industrial capability"),
    t("ped.p3", "Reduce external technological dependence"),
    t("ped.p4", "Support sovereign space initiatives"),
  ];

  const eedPoints = [
    t("eed.p1", "National ownership and control"),
    t("eed.p2", "Commitment to technological sovereignty"),
    t("eed.p3", "In-house engineering and development capability"),
    t("eed.p4", "Strategic relevance to Brazil's space structure"),
  ];

  return (
    <PageShell>
      {/* HERO SECTION */}
      <PageHero
        title={t("title", "STRATEGIC CERTIFICATION")}
        description={t("description",
          "Bizu Space is officially recognized through the PED and EED designations, affirming its strategic role in strengthening Brazil’s technological sovereignty and national space capability.",
        )}
        image={heroImage}
      />
      {/* PED SECTION */}
      <section className="relative overflow-hidden bg-black border-y border-accent text-white py-24 sm:py-32">
        <div className="container-bizu grid lg:grid-cols-[1.2fr_0.8fr] gap-12 lg:gap-20 items-center">
          <Reveal effect="left" className="space-y-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-accent">
                {t("ped.tag", "Strategic Product")}
              </span>
              <h2 className="display text-5xl sm:text-6xl md:text-7xl tracking-wider text-white mt-3">
                PED
              </h2>
            </div>

            <div className="space-y-6 text-lg leading-relaxed text-white/70">
              <p>
                {t("ped.desc",
                  "The PED classification is granted to products considered essential to Brazil's national space strategy.",
                )}
              </p>

              <div>
                <p className="font-semibold text-white/90 mb-4">
                  {t("ped.intro", "It recognizes technologies that:")}
                </p>
                <ul className="space-y-3 pl-2">
                  {pedPoints.map((point) => (
                    <li key={point} className="flex items-start">
                      <span className="text-accent mr-3 select-none font-bold text-lg">&gt;</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="pt-4 border-t border-white/10 italic text-white/80">
                {t("ped.footer",
                  "For BIZU Space, PED reinforces the strategic value of our aerospace technologies and propulsion systems.",
                )}
              </p>
            </div>
          </Reveal>

          <Reveal effect="right" className="flex justify-center lg:justify-end">
            <div className="relative group">
              <div className="absolute inset-0 bg-accent/20 rounded-full blur-2xl group-hover:bg-accent/35 transition duration-500" />
              <img
                src={pedSeal}
                alt="PED Seal"
                className="relative z-10 w-64 sm:w-80 h-auto object-contain transition duration-500 group-hover:scale-105"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* EED SECTION */}
      <section className="relative overflow-hidden bg-black border-b border-accent text-white py-24 sm:py-32">
        <div className="container-bizu grid lg:grid-cols-[1.2fr_0.8fr] gap-12 lg:gap-20 items-center">
          <Reveal effect="left" className="space-y-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-accent">
                {t("eed.tag", "Strategic Company")}
              </span>
              <h2 className="display text-5xl sm:text-6xl md:text-7xl tracking-wider text-white mt-3">
                EED
              </h2>
            </div>

            <div className="space-y-6 text-lg leading-relaxed text-white/70">
              <p>
                {t("eed.desc",
                  "The EED designation recognizes companies that develop and maintain technologies critical to the country.",
                )}
              </p>

              <div>
                <p className="font-semibold text-white/90 mb-4">
                  {t("eed.intro", "This status reflects:")}
                </p>
                <ul className="space-y-3 pl-2">
                  {eedPoints.map((point) => (
                    <li key={point} className="flex items-start">
                      <span className="text-accent mr-3 select-none font-bold text-lg">&gt;</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="pt-4 border-t border-white/10 italic text-white/80">
                {t("eed.footer",
                  "EED establishes BIZU Space as a strategic contributor to Brazil's long-term aerospace infrastructure.",
                )}
              </p>
            </div>
          </Reveal>

          <Reveal effect="right" className="flex justify-center lg:justify-end">
            <div className="relative group">
              <div className="absolute inset-0 bg-accent/20 rounded-full blur-2xl group-hover:bg-accent/35 transition duration-500" />
              <img
                src={eedSeal}
                alt="EED Seal"
                className="relative z-10 w-64 sm:w-80 h-auto object-contain transition duration-500 group-hover:scale-105"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection
        image={ctaImage}
        title={t("cta.title", "BIZU Space SPACE STRATEGIC PRODUCTS")}
        subtitle={t("cta.subtitle",
          "BIZU Space SPACE has 2 Strategic Products (PEDs): ARION Liquid Rocket Engine and the Liquid Training Rocket FTL.",
        )}
        buttonText={t("cta.btn1", "Explore ARION")}
        href="/products/arion-liquid-rocket-engine"
        secondaryButtonText={t("cta.btn2", "Explore FTL")}
        secondaryButtonHref="/services/ftl"
      />
    </PageShell>
  );
}
